import { Box, Chip } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { IAnalyticReportFilters } from '../../../../shared/services/analytic-report/analytic-report.model'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import CategoryService from '../../../../shared/services/category/category.service'
import GroupService from '../../../../shared/services/group/group.service'
import LocationService from '../../../../shared/services/location/location.service'
import ProjectService from '../../../../shared/services/project/project.service'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { IGroup } from '../../../../shared/services/group/group.model'
import { ICategory } from '../../../../shared/services/category/category.model'
import { ICountry, ILocation } from '../../../../shared/services/location/location.model'
import { IProject } from '../../../../shared/services/project/project.model'
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model'
import moment from 'moment'
import { AnalyticTooltipChip } from './analytic-tooltip-chip'
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service'
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model'
import DataTypeService from '../../../../shared/services/data-types/data-types.service'
import { IDataTypeRelation } from '../../../../shared/services/data-types/data-types.model'
import { getCategoryDisplayName, getCategoryGroups } from '../../../../shared/utils/category-utils'

export const AnalyticsFilterDisplay: FunctionComponent<{ filters: IAnalyticReportFilters, usingCustomDates: boolean, }> = ({ filters, usingCustomDates }) => {
  //Services
  const reportPeriodContext = useContext(ReportPeriodContext)
  const categoryService = new CategoryService()
  const groupService = GroupService.getInstance();
  const locationService = LocationService.getInstance();
  const projectService = ProjectService.getInstance()
  const subsidiaryService = SubsidiaryService.getInstance()
  const contactSupplierService = ContactSupplierService.getInstance();
  const dataTypeService = new DataTypeService();
  //State
  const [groups, setGroups] = useState<IGroup[]>()
  const [dataTypes, setDataTypes] = useState<IDataTypeRelation[]>()
  const [categories, setCategories] = useState<ICategory[]>()
  const [locations, setLocations] = useState<ILocation[]>()
  const [countries, setCountries] = useState<ICountry[]>()
  const [projects, setProjects] = useState<IProject[]>()
  const [subsidiaires, setSubsidiaires] = useState<ISubsidiary[]>()
  const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>()

  useEffect(() => {
    const { selectedFilters } = filters
    const needsDataTypes = selectedFilters.includes('Data Type') && filters.dataTypes.length > 0
    const needsGroups = selectedFilters.includes('Group') && filters.groups.length > 0
    // Category groups are read off the category list, so either filter needs it. categoryGroups is
    // absent on reports saved before that filter existed.
    const needsCategories = (selectedFilters.includes('Category') && filters.categories.length > 0)
      || (selectedFilters.includes('Category Group') && (filters.categoryGroups?.length ?? 0) > 0)
    const needsSubsidiaries = selectedFilters.includes('Subsidiary') && filters.subsidiaries.length > 0
    const needsProjects = selectedFilters.includes('Project') && filters.projects.length > 0
    const needsLocations = selectedFilters.includes('Location') && filters.locations.length > 0
    const needsCountries = selectedFilters.includes('Country') && filters.countries.length > 0
    const needsSuppliers = selectedFilters.includes('Supplier') && filters.suppliers.length > 0

    if (needsDataTypes) {
      dataTypeService.getAll()
        .then(response => setDataTypes(response))
        .catch(err => console.log(err))
    }
    if (needsGroups) {
      groupService.getTaggedGroups()
        .then(response => setGroups(response))
        .catch(err => console.log(err))
    }
    if (needsCategories) {
      categoryService.getAllCategories()
        .then(response => setCategories(response))
        .catch(err => console.log(err))
    }
    if (needsSubsidiaries) {
      subsidiaryService.getAll()
        .then(response => setSubsidiaires(response))
        .catch(err => console.log(err))
    }
    if (needsProjects) {
      projectService.getAllActive()
        .then(response => setProjects(response))
        .catch(err => console.log(err))
    }
    if (needsLocations) {
      locationService.getActiveLocations()
        .then(response => setLocations(response))
        .catch(err => console.log(err))
    }
    if (needsCountries) {
      locationService.getAllCountries()
        .then(response => setCountries(response))
        .catch(err => console.log(err))
    }
    if (needsSuppliers) {
      contactSupplierService.getActiveSuppliersLite(false)
        .then(response => setContactSuppliers(response))
        .catch(err => console.log(err))
    }
  }, [
    filters.selectedFilters,
    filters.groups.length,
    filters.categories.length,
    filters.categoryGroups?.length,
    filters.subsidiaries.length,
    filters.projects.length,
    filters.locations.length,
    filters.countries.length,
    filters.suppliers.length,
    filters.dataTypes.length
  ])

  return (
    <Box display='flex' flexDirection={'row'} alignItems={'flex-start'} flexWrap={'wrap'} gap={1}>
      {!usingCustomDates &&
        <Chip color='primary' variant='outlined' label={`Report Period: ${reportPeriodContext.allReportPeriods.find(reportPeriod => reportPeriod.id === filters.reportPeriodId)?.friendlyName}`} />
      }
      {usingCustomDates &&
        <>
          <Chip color='primary' variant='outlined' label={`Start Date: ${moment(filters.startDate).format('DD/MM/YYYY')}`} />
          <Chip color='primary' variant='outlined' label={`End Date: ${moment(filters.endDate).format('DD/MM/YYYY')}`} />
        </>}
      {filters.selectedFilters.map((filter, index) => {
        return (
          <>
            {filter === 'Scope' && filters.scopes.length > 0 && <Chip key={index} color='primary' variant='outlined'
              label={`${filter}: ${filters.scopes}`} />}
            {filter === 'Group' && groups && filters.groups.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.groups
                  .map(groupId => groups.find(g => g.id === groupId)?.title)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Category' && categories && filters.categories.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.categories
                  .map(categoryId => getCategoryDisplayName(categories.find(c => c.id === categoryId)) || undefined)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Category Group' && categories && (filters.categoryGroups?.length ?? 0) > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={(filters.categoryGroups ?? [])
                  .map(groupId => getCategoryGroups(categories).find(group => group.id === groupId)?.name)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Country' && countries && filters.countries.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.countries
                  .map(countryId => countries.find(c => c.id === countryId)?.name)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Subsidiary' && subsidiaires && filters.subsidiaries.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.subsidiaries
                  .map(subsidiaryId => subsidiaires.find(s => s.id === subsidiaryId)?.name)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Location' && locations && filters.locations.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.locations
                  .map(locationId => locations.find(l => l.id === locationId)?.friendlyName)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Project' && projects && filters.projects.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.projects
                  .map(projectId => projects.find(p => p.id === projectId)?.summary)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Supplier' && contactSuppliers && filters.suppliers.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.suppliers
                  .map(contactSupplierId => contactSuppliers.find(cs => cs.id === contactSupplierId)?.name)
                  .filter(Boolean)
                  .join(', ')}
              />}
            {filter === 'Data Type' && dataTypes && filters.dataTypes.length > 0 &&
              <AnalyticTooltipChip
                filter={filter}
                index={index}
                array={filters.dataTypes
                  .map(dataTypeId => dataTypes.find(dt => dt.id === dataTypeId)?.dataType)
                  .filter(Boolean)
                  .join(', ')}
              />}
          </>
        )
      })}
    </Box>
  )
}